/**
 * Parâmetros Settlement/Billing (AF-4126): edição do `value` de cada parâmetro de
 * `billing.business_properties`. Só ADMIN (rota e billing).
 *
 * O rascunho e o carregado ficam no formato canônico (JSON compacto): Salvar só habilita com
 * mudança de conteúdo e sem erro de validação; Reset volta ao carregado. O billing confere as
 * mesmas regras ao gravar, registra o autor e guarda o histórico.
 */
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ActionButton from '../../atoms/ActionButton';
import SelectTextField from '../../molecules/SelectTextField';
import PageForm from '../../templates/PageForm';
import BusinessPropertyEditor from './businessProperties/BusinessPropertyEditor';
import { BusinessProperty, listBusinessProperties, updateBusinessProperty } from './services/businessPropertiesApi';
import { canonicalValue, draftError, EDITABLE_PROPERTIES } from './services/businessPropertyRules';

const BREADCRUMB = [{ label: 'Configurações', url: '' }, { label: 'Parâmetros Settlement/Billing', url: '' }];

const loadProperties = async (
    setProperties: Dispatch<SetStateAction<BusinessProperty[]>>,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
) => {
    try {
        setProperties(await listBusinessProperties());
    } catch (error: any) {
        toast.error(error?.message || 'Não foi possível carregar os parâmetros');
    } finally {
        setIsLoading(false);
    }
};

const replaceProperty = (properties: BusinessProperty[], saved: BusinessProperty) =>
    properties.map(property => (property.name === saved.name ? saved : property));

interface SaveContext {
    name: string;
    draft: string;
    setProperties: Dispatch<SetStateAction<BusinessProperty[]>>;
    setDraft: Dispatch<SetStateAction<string>>;
    setSaving: Dispatch<SetStateAction<boolean>>;
}

const saveProperty = async ({ name, draft, setProperties, setDraft, setSaving }: SaveContext) => {
    setSaving(true);
    try {
        const saved = await updateBusinessProperty(name, draft);
        setProperties(previous => replaceProperty(previous, saved));
        setDraft(canonicalValue(saved.name, saved.value));
        toast.success('Parâmetro salvo');
    } catch (failure: any) {
        toast.error(failure?.message || 'Não foi possível salvar o parâmetro');
    } finally {
        setSaving(false);
    }
};

const formatUpdate = (property: BusinessProperty) => {
    if (!property.updatedBy) return null;
    const when = property.updatedAt ? new Date(property.updatedAt).toLocaleString('pt-BR') : '';
    return `Última alteração: ${property.updatedBy}${when ? ` em ${when}` : ''}`;
};

const BusinessPropertiesPage = () => {
    const [properties, setProperties] = useState<BusinessProperty[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState('');
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadProperties(setProperties, setIsLoading);
    }, []);

    const current = properties.find(property => property.name === selected);
    const loaded = current ? canonicalValue(current.name, current.value) : '';
    const error = current ? draftError(current.name, draft) : null;
    const canSave = Boolean(current) && draft !== loaded && !error && !saving;
    const options = EDITABLE_PROPERTIES
        .filter(name => properties.some(property => property.name === name))
        .map(name => ({ value: name, label: name }));
    const lastUpdate = current ? formatUpdate(current) : null;

    return (
        <PageForm
            pageTitle="Parâmetros Settlement/Billing"
            apiVersion=""
            isLoading={isLoading}
            breadcrumb={BREADCRUMB}
            controlSlot={null}
        >
            <div className="flex flex-col gap-4 p-4 max-w-5xl">
                <SelectTextField
                    id="business-property"
                    label="Parâmetro"
                    items={options}
                    emptyLabel="Selecione"
                    value={selected}
                    onChange={e => {
                        const property = properties.find(item => item.name === e.target.value);
                        setSelected(e.target.value);
                        setDraft(property ? canonicalValue(property.name, property.value) : '');
                    }}
                />
                {current && (
                    <>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{current.description}</p>
                        <BusinessPropertyEditor name={current.name} value={draft} onChange={setDraft} error={error} />
                        {lastUpdate && <p className="text-xs text-gray-400">{lastUpdate}</p>}
                        <div className="flex justify-end gap-2">
                            <ActionButton variant="secondary" disabled={saving} onClick={() => setDraft(loaded)}>Reset</ActionButton>
                            <ActionButton
                                disabled={!canSave}
                                loading={saving}
                                onClick={() => saveProperty({
                                    name: current.name, draft, setProperties, setDraft, setSaving,
                                })}
                            >
                                Salvar
                            </ActionButton>
                        </div>
                    </>
                )}
            </div>
        </PageForm>
    );
};

export default BusinessPropertiesPage;
